import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import SessionTimeOutDailog from "../components/Page-Components/Dailog-Component/SessionTimeOutDailog/SessionTimeOutDailog";
import { useAuthLogout } from "./authLogout";

const INACTIVITY_LIMIT = 10 * 60 * 1000; // 10 min
const WARNING_TIME = 58 * 60 * 1000; // 58 min
const SESSION_LIMIT = 60 * 60 * 1000; // 60 min

export default function SessionManager() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { appLogout } = useAuthLogout();

  const inactivityTimer = useRef(null);
  const warningTimer = useRef(null);
  const sessionTimer = useRef(null);

  const lastActivityTime = useRef(Date.now());
  const sessionStartTime = useRef(Date.now());

  const [showWarning, setShowWarning] = useState(false);

  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

  const checkInactivity = useCallback(() => {
    const inactiveTime = Date.now() - lastActivityTime.current;

    console.log("Inactive for:", inactiveTime);
    console.log("Inactivity limit:", INACTIVITY_LIMIT);
    if (inactiveTime >= INACTIVITY_LIMIT) {
      console.log("Logging out due to inactivity");
      appLogout();
    }
  }, [appLogout]);

  const resetInactivityTimer = useCallback(() => {
    lastActivityTime.current = Date.now();

    clearTimeout(inactivityTimer.current);

    inactivityTimer.current = setTimeout(() => {
      checkInactivity();
    }, INACTIVITY_LIMIT);
  }, [checkInactivity]);

  const startTimers = useCallback(() => {
    lastActivityTime.current = Date.now();
    sessionStartTime.current = Date.now();

    clearTimeout(inactivityTimer.current);
    clearTimeout(warningTimer.current);
    clearTimeout(sessionTimer.current);

    warningTimer.current = setTimeout(() => {
      console.log("Showing warning dialog");
      setShowWarning(true);
    }, WARNING_TIME);

    sessionTimer.current = setTimeout(() => {
      console.log("Session expired");
      appLogout();
    }, SESSION_LIMIT);

    resetInactivityTimer();
  }, [appLogout, resetInactivityTimer]);

  const handleStaySignedIn = async () => {
    try {
      await getAccessTokenSilently({
        ignoreCache: true,
      });

      setShowWarning(false);

      startTimers();
    } catch (err) {
      console.error("Token refresh failed", err);
      appLogout();
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    startTimers();

    const events = isMobile
      ? ["touchstart", "touchmove", "touchend"]
      : ["click", "mousemove", "keydown", "scroll"];

    events.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer);
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("App became visible");
        checkInactivity();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimeout(inactivityTimer.current);
      clearTimeout(warningTimer.current);
      clearTimeout(sessionTimer.current);

      events.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer);
      });

      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    isAuthenticated,
    isMobile,
    startTimers,
    resetInactivityTimer,
    checkInactivity,
  ]);

  return (
    <SessionTimeOutDailog
      open={showWarning}
      onClose={() => setShowWarning(false)}
      onStaySignedIn={handleStaySignedIn}
      onSignOut={appLogout}
    />
  );
}
